import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// ── Public ───────────────────────────────────────────────────────────────────
import LandingPage from './pages/LandingPage';
import ExpertsPage from './pages/ExpertsPage';
import ExpertProfilePage from './pages/ExpertProfilePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';

// ── Client ───────────────────────────────────────────────────────────────────
import DashboardPage from './pages/DashboardPage';
import BookingsPage from './pages/BookingsPage';
import BookingDetailPage from './pages/BookingDetailPage';
import ProgressPage from './pages/ProgressPage';
import NotificationsPage from './pages/NotificationsPage';
import SettingsPage from './pages/SettingsPage';
import CanvasPage from './pages/CanvasPage';
import SessionRoomPage from './pages/SessionRoomPage';

// ── Expert ───────────────────────────────────────────────────────────────────
import ExpertDashboardPage from './pages/ExpertDashboardPage';
import ExpertSessionsPage from './pages/ExpertSessionsPage';
import ExpertSubmissionsPage from './pages/ExpertSubmissionsPage';

// ── Admin ────────────────────────────────────────────────────────────────────
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminExpertsPage from './pages/AdminExpertsPage';
import AdminClientsPage from './pages/AdminClientsPage';
import AdminSessionsPage from './pages/AdminSessionsPage';
import AdminBookingsPage from './pages/AdminBookingsPage';
import AdminGroupSessionsPage from './pages/AdminGroupSessionsPage';
import AdminDocumentsPage from './pages/Admindocumentspage';
import AdminAuditLogPage from './pages/Adminauditlogpage';
import AdminBroadcastPage from './pages/AdminBroadcastPage';
import TimesheetPage from './pages/TimesheetPage';
import DocumentsPage from './pages/DocumentsPage';
import AdminLevelsPage from './pages/AdminLevelsPage';
import SessionRecordingPage from './pages/SessionRecordingPage';
import ClientSessionsPage from './pages/ClientSessionsPage';
import AboutPage from './pages/AboutPage';
import HowItWorksPage from './pages/HowItWorksPage';
import CategoriesPage from './pages/CategoriesPage';
import AdminReportsPage from './pages/AdminReportsPage';
import AssessmentPage       from './pages/AssessmentPage';
import AdminAssessmentsPage from './pages/AdminAssessmentsPage';
import AdminSettingsPage    from './pages/Adminsettingspage';
import AdminSeminarsPage    from './pages/Adminseminarspage';
import AdminCohortsPage     from './pages/AdminCohortsPage';
import CohortClassroomPage   from './pages/CohortClassroomPage';
import ExpertCohortsPage     from './pages/ExpertCohortsPage';
import StudentCohortPage     from './pages/StudentCohortPage';
import CohortLectureRoomPage from './pages/CohortLectureRoomPage';
import SeminarRoomPage      from './pages/Seminarroompage';
import CommunitiesPage      from './pages/CommunitiesPage';
import CommunityDetailPage  from './pages/CommunityDetailPage';
import CommunityCallPage    from './pages/CommunityCallPage';
// import CommunitiesPage      from './pages/CommunitiesPage';
// import CommunityDetailPage  from './pages/CommunityDetailPage';

// import HelpPage       from './pages/HelpPage';
// import AdminHelpPage  from './pages/AdminHelpPage';

// ── Group Sessions ────────────────────────────────────────────────────────────
import GroupSessionsPage      from './pages/GroupSessionsPage';
import CreateGroupSessionPage from './pages/CreateGroupSessionPage';
import GroupSessionDetailPage from './pages/GroupSessionDetailPage';
import GroupSessionRoomPage   from './pages/GroupSessionRoomPage';
import AdminHelpPage from './pages/Adminhelppage';
import HelpPage from './pages/HelpPage';
import CompletedProjectsPage        from './pages/CompletedProjectsPage';
import AdminCompletedProjectsPage   from './pages/AdminCompletedProjectsPage';

// ── Route guards ─────────────────────────────────────────────────────────────
const Spinner = () => (
  <div className="min-h-screen bg-cream-50 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-forest-600 border-t-transparent rounded-full animate-spin" />
  </div>
);

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  return user ? children : <Navigate to="/login" replace />;
};

const GuestRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return !user ? children : <Navigate to="/dashboard" replace />;
};

const ExpertRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'EXPERT') return <Navigate to="/dashboard" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'SUPER_ADMIN') return <Navigate to="/dashboard" replace />;
  return children;
};

// CLIENT only — experts and admins redirected to their dashboard
const ClientRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'EXPERT') return <Navigate to="/expert/dashboard" replace />;
  if (user.role === 'SUPER_ADMIN') return <Navigate to="/admin" replace />;
  return children;
};

// Redirects each role to the right home after login
const RoleRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'EXPERT') return <Navigate to="/expert/dashboard" replace />;
  if (user?.role === 'SUPER_ADMIN') return <Navigate to="/admin" replace />;
  return <Navigate to="/dashboard" replace />;
};

const RoleAwareHome = () => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <LandingPage />;
  if (user.role === 'EXPERT') return <Navigate to="/expert/dashboard" replace />;
  if (user.role === 'SUPER_ADMIN') return <Navigate to="/admin" replace />;
  return <Navigate to="/dashboard" replace />;
};

// ── Routes ───────────────────────────────────────────────────────────────────
const AppRoutes = () => (
  <Routes>

    {/* ── Public ── */}
    <Route path="/" element={<RoleAwareHome />} />
    <Route path="/about"        element={<AboutPage />} />
    <Route path="/completed-projects" element={<CompletedProjectsPage />} />
    <Route path="/seminar/:code"  element={<SeminarRoomPage />} />
    <Route path="/communities"        element={<PrivateRoute><CommunitiesPage /></PrivateRoute>} />
    <Route path="/communities/:id"    element={<PrivateRoute><CommunityDetailPage /></PrivateRoute>} />
    <Route path="/how-it-works" element={<HowItWorksPage />} />
    <Route path="/categories"   element={<CategoriesPage />} />
    <Route path="/experts"      element={<ExpertsPage />} />
    <Route path="/experts/:id"  element={<ExpertProfilePage />} />

    {/* ── Auth (guest only) ── */}
    <Route path="/login"            element={<GuestRoute><LoginPage /></GuestRoute>} />
    <Route path="/register"         element={<GuestRoute><RegisterPage /></GuestRoute>} />
    <Route path="/forgot-password"  element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
    <Route path="/reset-password"   element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />
    <Route path="/verify-email"     element={<VerifyEmailPage />} />

    {/* ── Role redirect ── */}
    <Route path="/home" element={<PrivateRoute><RoleRedirect /></PrivateRoute>} />

    {/* ── Client ── */}
    <Route path="/dashboard"               element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
    <Route path="/bookings"                element={<PrivateRoute><BookingsPage /></PrivateRoute>} />
    <Route path="/bookings/:id"            element={<PrivateRoute><BookingDetailPage /></PrivateRoute>} />
    <Route path="/progress"                element={<ClientRoute><ProgressPage /></ClientRoute>} />
    <Route path="/documents"               element={<ClientRoute><DocumentsPage /></ClientRoute>} />
    <Route path="/timesheet"               element={<ClientRoute><TimesheetPage role="CLIENT" /></ClientRoute>} />
    <Route path="/notifications"           element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
    <Route path="/settings"                element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
    <Route path="/canvas/:levelId"         element={<ClientRoute><CanvasPage /></ClientRoute>} />
    <Route path="/sessions/:id"            element={<PrivateRoute><SessionRoomPage /></PrivateRoute>} />
    <Route path="/sessions/:id/recording"  element={<PrivateRoute><SessionRecordingPage /></PrivateRoute>} />
    <Route path="/my-sessions"             element={<ClientRoute><ClientSessionsPage /></ClientRoute>} />
    <Route path="/assessment"              element={<PrivateRoute><AssessmentPage /></PrivateRoute>} />
    <Route path="/help" element={<PrivateRoute><HelpPage /></PrivateRoute>} />

    {/* ── Group Sessions (all authenticated users) ── */}
    <Route path="/group-sessions"         element={<PrivateRoute><GroupSessionsPage /></PrivateRoute>} />
    <Route path="/group-sessions/create"  element={<PrivateRoute><CreateGroupSessionPage /></PrivateRoute>} />
    <Route path="/group-sessions/:id"     element={<PrivateRoute><GroupSessionDetailPage /></PrivateRoute>} />
    <Route path="/group-sessions/:id/room" element={<PrivateRoute><GroupSessionRoomPage /></PrivateRoute>} />
    {/* ── Communities ── */}
    <Route path="/communities"          element={<PrivateRoute><CommunitiesPage /></PrivateRoute>} />
    <Route path="/communities/:id"      element={<PrivateRoute><CommunityDetailPage /></PrivateRoute>} />
    <Route path="/communities/:id/call" element={<PrivateRoute><CommunityCallPage /></PrivateRoute>} />

    {/* ── Cohorts Classroom ── */}
    <Route path="/my-cohort"                      element={<PrivateRoute><StudentCohortPage /></PrivateRoute>} />
    <Route path="/cohorts/:id"                    element={<PrivateRoute><CohortClassroomPage /></PrivateRoute>} />
    <Route path="/cohorts/:id/lecture/:lectureId" element={<PrivateRoute><CohortLectureRoomPage /></PrivateRoute>} />

    {/* ── Expert ── */}
    <Route path="/expert/dashboard"               element={<ExpertRoute><ExpertDashboardPage /></ExpertRoute>} />
    <Route path="/expert/cohorts"                 element={<ExpertRoute><ExpertCohortsPage /></ExpertRoute>} />
    <Route path="/expert/cohorts/:id"             element={<ExpertRoute><CohortClassroomPage /></ExpertRoute>} />
    <Route path="/expert/bookings"                element={<ExpertRoute><BookingsPage /></ExpertRoute>} />
    <Route path="/expert/bookings/:id"            element={<ExpertRoute><BookingDetailPage /></ExpertRoute>} />
    <Route path="/expert/sessions"                element={<ExpertRoute><ExpertSessionsPage /></ExpertRoute>} />
    <Route path="/expert/timesheet"               element={<ExpertRoute><TimesheetPage role="EXPERT" /></ExpertRoute>} />
    <Route path="/expert/submissions"             element={<ExpertRoute><ExpertSubmissionsPage /></ExpertRoute>} />
    <Route path="/expert/sessions/:id/recording"  element={<ExpertRoute><SessionRecordingPage /></ExpertRoute>} />

    {/* ── Admin ── */}
    <Route path="/admin"              element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
    <Route path="/admin/experts"      element={<AdminRoute><AdminExpertsPage /></AdminRoute>} />
    <Route path="/admin/clients"      element={<AdminRoute><AdminClientsPage /></AdminRoute>} />
    <Route path="/admin/sessions"     element={<AdminRoute><AdminSessionsPage /></AdminRoute>} />
    <Route path="/admin/bookings"     element={<AdminRoute><AdminBookingsPage /></AdminRoute>} />
    <Route path="/admin/group-sessions" element={<AdminRoute><AdminGroupSessionsPage /></AdminRoute>} />
    <Route path="/admin/documents"    element={<AdminRoute><AdminDocumentsPage /></AdminRoute>} />
    <Route path="/admin/audit-log"    element={<AdminRoute><AdminAuditLogPage /></AdminRoute>} />
    <Route path="/admin/broadcast"    element={<AdminRoute><AdminBroadcastPage /></AdminRoute>} />
    <Route path="/admin/levels"       element={<AdminRoute><AdminLevelsPage /></AdminRoute>} />
    <Route path="/admin/reports"      element={<AdminRoute><AdminReportsPage /></AdminRoute>} />
    <Route path="/admin/assessments"  element={<AdminRoute><AdminAssessmentsPage /></AdminRoute>} />
    <Route path="/admin/settings"      element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
    <Route path="/admin/seminars"      element={<AdminRoute><AdminSeminarsPage /></AdminRoute>} />
    <Route path="/admin/cohorts"       element={<AdminRoute><AdminCohortsPage /></AdminRoute>} />
    <Route path="/admin/cohorts/:id"   element={<AdminRoute><CohortClassroomPage /></AdminRoute>} />
    <Route path="/admin/help" element={<AdminRoute><AdminHelpPage /></AdminRoute>} />
    <Route path="/admin/completed-projects" element={<AdminRoute><AdminCompletedProjectsPage /></AdminRoute>} />

    {/* ── 404 ── */}
    <Route path="*" element={
      <div className="min-h-screen flex items-center justify-center bg-cream-50">
        <div className="text-center space-y-4">
          <h1 className="font-serif text-7xl font-semibold text-forest-900">404</h1>
          <p className="font-sans text-forest-500">This page doesn't exist.</p>
          <a href="/" className="btn-primary inline-flex">← Back to Home</a>
        </div>
      </div>
    } />

  </Routes>
);

// ── App ──────────────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { fontFamily: 'DM Sans, sans-serif', borderRadius: '12px', background: '#0b4c37', color: '#fdfaf5' },
            success: { iconTheme: { primary: '#4ea34e', secondary: '#fdfaf5' } },
            error: { style: { background: '#a8431f', color: '#fdfaf5' } },
          }}
        />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;